/**
 * CompanyRolesEditor — role osoby w JEDNEJ firmie: jedna lub wiele, z kolejnością (ADR-0103).
 *
 * Chipy ról w kolejności nadanej przez administratora. Pierwsza rola jest GŁÓWNA — to ona
 * rozstrzyga np. który układ formularza osoba dostaje, gdy kilka ról ma własny układ. Kolejność
 * zmienia się przeciąganiem chipa; krzyżyk odbiera rolę; „Dodaj rolę" podaje wyłącznie role TEJ
 * firmy, których osoba jeszcze nie ma (każda firma ma własne role — obcej nie da się przypiąć).
 *
 * Komponent jest bezstanowy wobec serwera: każda zmiana to `onChange(nowaListaWKolejności)`,
 * a zapis (jedno żądanie „ustaw komplet ról") robi rodzic.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { cn } from '@/lib/utils';

export interface CompanyRoleOption {
  id: string;
  name: string;
  isSystem?: boolean;
}

export interface CompanyRolesEditorProps {
  /** Firma, której dotyczą role — do identyfikatorów testowych. */
  companyId: string;
  /** Role osoby w tej firmie, w kolejności (pierwsza = główna). */
  assigned: Array<{ id: string; name: string }>;
  /** Wszystkie role TEJ firmy (do „Dodaj rolę"). */
  available: CompanyRoleOption[];
  onChange: (nextRoleIds: string[]) => void;
  busy?: boolean;
  /**
   * Klucz identyfikatorów testowych, gdy na ekranie jest kilka edytorów TEJ SAMEJ firmy
   * (np. lista członków firmy — wiersz na osobę). Domyślnie `companyId`.
   */
  testIdKey?: string;
}

export function CompanyRolesEditor({
  companyId,
  assigned,
  available,
  onChange,
  busy = false,
  testIdKey,
}: CompanyRolesEditorProps) {
  const idKey = testIdKey ?? companyId;
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Lista „Dodaj rolę" nie trzyma wyboru — po dodaniu wraca do podpowiedzi (nowy klucz = reset).
  const [addPickerKey, setAddPickerKey] = useState(0);
  const assignedIds = assigned.map((r) => r.id);
  const addable = available.filter((r) => !assignedIds.includes(r.id));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = assignedIds.indexOf(String(active.id));
    const to = assignedIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onChange(arrayMove(assignedIds, from, to));
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`company-roles-${idKey}`}>
      {assigned.length === 0 && (
        <span className="text-xs text-text-tertiary" data-testid={`company-roles-empty-${idKey}`}>
          {t('tenant.users.roles.none')}
        </span>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={assignedIds} strategy={horizontalListSortingStrategy}>
          {assigned.map((role, index) => (
            <RoleChip
              key={role.id}
              id={role.id}
              name={role.name}
              primary={index === 0 && assigned.length > 1}
              disabled={busy}
              onRemove={() => onChange(assignedIds.filter((id) => id !== role.id))}
            />
          ))}
        </SortableContext>
      </DndContext>

      {addable.length > 0 && (
        <Dropdown
          key={addPickerKey}
          value={undefined}
          onChange={(roleId) => {
            if (roleId) onChange([...assignedIds, roleId]);
            setAddPickerKey((k) => k + 1);
          }}
          disabled={busy}
          placeholder={`+ ${t('tenant.users.roles.add')}`}
          className="w-40"
          aria-label={t('tenant.users.roles.add')}
          data-testid={`company-roles-add-${idKey}`}
          options={addable.map((r) => ({
            value: r.id,
            label: r.isSystem ? `${r.name} ${t('tenant.permissions.role.systemSuffix')}` : r.name,
          }))}
        />
      )}
    </div>
  );
}

function RoleChip({
  id,
  name,
  primary,
  disabled,
  onRemove,
}: {
  id: string;
  name: string;
  primary: boolean;
  disabled: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <span
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg border border-purple-200 bg-purple-50 py-0.5 pl-1 pr-0.5 text-xs font-medium text-purple-800 dark:border-purple-800 dark:bg-purple-900/30 dark:text-purple-200',
        isDragging && 'z-10 opacity-80 shadow',
      )}
      data-testid={`role-chip-${id}`}
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-purple-400 active:cursor-grabbing"
        aria-label={t('tenant.users.roles.reorder', { name })}
      >
        <GripVertical size={12} aria-hidden="true" />
      </span>
      <span className="px-0.5">{name}</span>
      {primary && (
        <Badge tone="purple" size="sm" data-testid={`role-chip-primary-${id}`}>
          {t('tenant.users.roles.primary')}
        </Badge>
      )}
      <IconButton
        size="sm"
        variant="ghost"
        className="h-5 w-5 [&_svg]:h-3 [&_svg]:w-3"
        onClick={onRemove}
        disabled={disabled}
        aria-label={t('tenant.users.roles.remove', { name })}
        data-testid={`role-chip-remove-${id}`}
      >
        <X aria-hidden="true" />
      </IconButton>
    </span>
  );
}
